import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Check, Loader2, GraduationCap } from 'lucide-react';
import { TopicsStep, AssetsStep, ReviewStep } from './onboarding/DeepOnboardingSteps';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStepStyle = (i, currentStep) => {
  if (i < currentStep) return 'bg-emerald-600 text-white';
  if (i === currentStep) return 'border-2 border-emerald-600 text-emerald-600';
  return 'border-2 border-slate-200';
};

export default function DeepOnboardingModal({ isOpen, onClose, onSuccess }) {
  const [options, setOptions] = useState({ help_topics: [], asset_types: [] });
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [description, setDescription] = useState('');
  const [maskedMode, setMaskedMode] = useState(true);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('auth_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (isOpen) {
      setStep(0); setSelectedTopics([]); setSelectedAssets([]);
      setDescription(''); setMaskedMode(true);
      fetch(`${BACKEND_URL}/api/assisted-onboarding/options`, { headers, cache: 'no-store' })
        .then(r => r.json()).then(setOptions).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggleItem = (list, setList, item) => setList(prev => prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/deep/request`, {
        method: 'POST', headers,
        body: JSON.stringify({ help_topics: selectedTopics, asset_types: selectedAssets, description: description || null, masked_mode: maskedMode })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      onSuccess?.(await res.json());
      onClose();
    } catch (err) { alert(err.message); }
    finally { setSubmitting(false); }
  };

  const STEPS = ['What', 'Assets', 'Review'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="deep-onboarding-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-emerald-600" />Private Virtual Deep Onboarding
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-1">Tell us what you need help with. We'll review your request and get back to you.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 px-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${i <= step ? 'text-emerald-600' : 'text-slate-300'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${getStepStyle(i, step)}`}>
                  {i < step ? <Check className="w-3 h-3" /> : i + 1}
                </div>
                <span className="capitalize hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-emerald-600' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>
        <div className="min-h-[280px]">
          {step === 0 && <TopicsStep options={options} selectedTopics={selectedTopics} onToggle={(t) => toggleItem(selectedTopics, setSelectedTopics, t)} />}
          {step === 1 && <AssetsStep options={options} selectedAssets={selectedAssets} onToggle={(a) => toggleItem(selectedAssets, setSelectedAssets, a)} description={description} setDescription={setDescription} />}
          {step === 2 && <ReviewStep selectedTopics={selectedTopics} selectedAssets={selectedAssets} description={description} maskedMode={maskedMode} setMaskedMode={setMaskedMode} />}
        </div>
        <DialogFooter className="flex gap-2">
          {step > 0 && <Button variant="outline" onClick={() => setStep(s => s - 1)} data-testid="deep-back-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}
          <div className="flex-1" />
          {step < 2 ? (
            <Button onClick={() => setStep(s => s + 1)} disabled={(step === 0 && selectedTopics.length === 0) || (step === 1 && selectedAssets.length === 0)}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="deep-next-btn">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="deep-submit-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Submit Request</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
